import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import Layout from '../components/layout/Layout';
import SlidePanel from '../components/common/SlidePanel';
import Badge from '../components/common/Badge';
import { Input, Select, Textarea } from '../components/common/FormField';
import PatientSearchInput from '../components/patient/PatientSearchInput';
import FileShaClaimButton from '../components/common/FileShaClaimButton';
import api from '../services/api';
import { fmtDate, fmtDateTime } from '../utils/helpers';
import {useTranslation} from 'react-i18next';

const TABS = [['anc', '🤰 ANC Visits'], ['deliveries', '🏥 Deliveries & Newborns']];
const MODE_OF_DELIVERY = ['SVD', 'ASSISTED_VACUUM', 'ASSISTED_FORCEPS', 'CAESAREAN_SECTION', 'BREECH'];
const OUTCOMES = ['LIVE_BIRTH', 'STILLBIRTH', 'NEONATAL_DEATH', 'MATERNAL_DEATH'];
const FEEDING_TYPES = ['EXCLUSIVE_BREASTFEEDING', 'MIXED', 'FORMULA', 'NPO'];

export default function MaternityPage() {
  const {t}=useTranslation();
  const { user } = useAuth();
  const [tab, setTab] = useState('anc');
  const [stats, setStats] = useState<any>({});
  const [patient, setPatient] = useState<any>(null);
  const [ancVisits, setAncVisits] = useState<any[]>([]);
  const [deliveries, setDeliveries] = useState<any[]>([]);
  const [selDelivery, setSelDelivery] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const notify = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };

  const [showAnc, setShowAnc] = useState(false);
  const defaultAncForm = { patientId: '', visitDate: new Date().toISOString().slice(0, 10), gestationalWeeks: '', weightKg: '', bloodPressure: '', fundalHeightCm: '', fetalHeartRate: '', fetalPresentation: '', urinalysis: '', hemoglobin: '', tetanusToxoidGiven: false, complaints: '', notes: '', nextVisitDate: '' };
  const [ancForm, setAncForm] = useState<any>(defaultAncForm);

  const [showDelivery, setShowDelivery] = useState(false);
  const defaultDeliveryForm = { patientId: '', onsetType: 'SPONTANEOUS', deliveredAt: '', modeOfDelivery: 'SVD', caesareanIndication: '', perinealStatus: '', placentaDelivery: '', estimatedBloodLossMl: '', complications: '', notes: '', outcome: 'LIVE_BIRTH' };
  const [deliveryForm, setDeliveryForm] = useState<any>(defaultDeliveryForm);

  const [showPartograph, setShowPartograph] = useState(false);
  const [partographForm, setPartographForm] = useState<any>({ time: '', cervicalDilationCm: '', contractionsPer10Min: '', fetalHeartRate: '', notes: '' });

  const [showNewborn, setShowNewborn] = useState(false);
  const defaultNewbornForm = { deliveryRecordId: '', motherPatientId: '', babyName: '', sex: 'FEMALE', birthWeightKg: '', birthTime: '', apgar1Min: '', apgar5Min: '', apgar10Min: '', resuscitationRequired: false, resuscitationDetails: '', congenitalAnomalies: '', vitaminKGiven: true, bcgGiven: true, feedingType: 'EXCLUSIVE_BREASTFEEDING', birthNotificationNumber: '', notes: '' };
  const [newbornForm, setNewbornForm] = useState<any>(defaultNewbornForm);

  const loadStats = useCallback(async () => {
    try { const r = await api.getMaternityStats(); setStats(r?.data || {}); } catch { }
  }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  const loadAnc = useCallback(async (patientId: string) => {
    try {
      const r = await api.getAncVisits(patientId);
      setAncVisits(Array.isArray(r?.data) ? r.data : (r?.data?.data || []));
    } catch { setAncVisits([]); }
  }, []);

  const loadDeliveries = useCallback(async (patientId?: string) => {
    try {
      const r = await api.getDeliveries(patientId ? { patientId } : {});
      setDeliveries(Array.isArray(r?.data) ? r.data : (r?.data?.data || []));
    } catch { setDeliveries([]); }
  }, []);

  useEffect(() => {
    if (patient) {
      loadAnc(patient.id);
      loadDeliveries(patient.id);
      setSelDelivery(null);
    } else {
      setAncVisits([]);
      loadDeliveries();
    }
  }, [patient, loadAnc, loadDeliveries]);

  const openDeliveryDetail = async (d: any) => {
    try {
      const r = await api.getDeliveryFull(d.id);
      const full = r?.data;
      // getDeliveryFull() returns {delivery, newborns} from a plain repo lookup — no
      // patient-name join like the list endpoint has — so carry those over from `d`.
      setSelDelivery(full?.delivery ? { first_name: d.first_name, last_name: d.last_name, ...full.delivery, newborns: full.newborns || [] } : d);
    } catch { setSelDelivery(d); }
  };

  const saveAnc = async () => {
    if (!patient) { notify('⚠️ Select a patient first'); return; }
    if (!ancForm.visitDate) { notify('⚠️ Visit date required'); return; }
    setSaving(true);
    try {
      await api.createAncVisit({ ...ancForm, patientId: patient.id, doctorId: user?.id });
      notify('✅ ANC visit recorded!'); setShowAnc(false); setAncForm(defaultAncForm);
      loadAnc(patient.id); loadStats();
    } catch (e: any) { notify('❌ ' + (e.response?.data?.message || 'Error')); }
    setSaving(false);
  };

  const saveDelivery = async () => {
    if (!patient) { notify('⚠️ Select a patient first'); return; }
    if (!deliveryForm.deliveredAt) { notify('⚠️ Delivery date/time required'); return; }
    setSaving(true);
    try {
      const r = await api.createDelivery({ ...deliveryForm, patientId: patient.id, doctorId: user?.id });
      notify('✅ Delivery record created!'); setShowDelivery(false); setDeliveryForm(defaultDeliveryForm);
      await loadDeliveries(patient.id); loadStats();
      if (r?.data?.id) openDeliveryDetail(r.data);
    } catch (e: any) { notify('❌ ' + (e.response?.data?.message || 'Error')); }
    setSaving(false);
  };

  const savePartograph = async () => {
    if (!selDelivery) return;
    setSaving(true);
    try {
      await api.addPartographEntry(selDelivery.id, partographForm);
      notify('✅ Partograph entry added!'); setShowPartograph(false);
      setPartographForm({ time: '', cervicalDilationCm: '', contractionsPer10Min: '', fetalHeartRate: '', notes: '' });
      openDeliveryDetail(selDelivery);
    } catch (e: any) { notify('❌ ' + (e.response?.data?.message || 'Error')); }
    setSaving(false);
  };

  const saveNewborn = async () => {
    if (!selDelivery) return;
    setSaving(true);
    try {
      await api.createNewborn({ ...newbornForm, deliveryRecordId: selDelivery.id, motherPatientId: patient?.id || selDelivery.patient_id || selDelivery.patientId });
      notify('✅ Newborn record created!'); setShowNewborn(false); setNewbornForm(defaultNewbornForm);
      openDeliveryDetail(selDelivery); loadStats();
    } catch (e: any) { notify('❌ ' + (e.response?.data?.message || 'Error')); }
    setSaving(false);
  };

  const partographLog: any[] = selDelivery?.partograph_log || selDelivery?.partographLog || [];
  const newborns: any[] = selDelivery?.newborns || [];

  return (
    <Layout title="Maternity & Newborn Health" actions={
      <div className="flex gap-2">
        <button onClick={() => { setDeliveryForm({ ...defaultDeliveryForm, deliveredAt: new Date().toISOString().slice(0, 16) }); setShowDelivery(true); }} className="btn-secondary text-sm">🏥 New Delivery</button>
        <button onClick={() => { setAncForm(defaultAncForm); setShowAnc(true); }} className="btn-primary text-sm">+ New ANC Visit</button>
      </div>
    }>
      {msg && <div className="fixed top-4 right-4 bg-white border shadow-xl rounded-xl px-4 py-3 text-sm z-50">{msg}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {[['Deliveries Today', stats.deliveriesToday ?? '—', 'blue'], ['Active ANC Patients', stats.activeAncPatients ?? '—', 'purple'], ['Caesarean Rate (30d)', stats.caesareanRate30d !== undefined ? `${Math.round(stats.caesareanRate30d * 100)}%` : '—', 'amber'], ['Live Births This Month', stats.liveBirthsThisMonth ?? '—', 'green']].map(([l, v, c]) => (
          <div key={String(l)} className="card p-3 text-center"><p className="text-xs text-slate-400">{l}</p><p className={`text-2xl font-bold text-${c}-600`}>{v}</p></div>
        ))}
      </div>

      <div className="card p-3 mb-4">
        <PatientSearchInput label="Patient" selected={patient} onSelect={setPatient} onClear={() => setPatient(null)} />
      </div>

      <div className="flex gap-1 mb-4 bg-slate-100 rounded-xl p-1 overflow-x-auto">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`flex-shrink-0 text-sm py-2 px-3 rounded-lg transition-all ${tab === k ? 'bg-white shadow font-semibold text-[#02357A]' : 'text-slate-600 hover:text-slate-800'}`}>{l}</button>
        ))}
      </div>

      {tab === 'anc' && (
        !patient ? (
          <div className="card p-12 text-center text-slate-400"><span className="text-4xl block mb-2">🤰</span>Select a patient above to view/record ANC visits</div>
        ) : (
          <div className="card overflow-hidden">
            {ancVisits.length > 0 && (
              <div className="p-2 bg-blue-50/50 border-b flex justify-end">
                <FileShaClaimButton
                  patientId={patient.id}
                  patientName={`${patient.first_name || patient.firstName || ''} ${patient.last_name || patient.lastName || ''}`.trim()}
                  patientCrId={patient.sha_beneficiary_cr_id || patient.shaBeneficiaryCrId}
                  encounterId={ancVisits[0].id}
                  serviceName="Antenatal care visit"
                  from="maternity"
                />
              </div>
            )}
            <div className="p-3 bg-slate-50 border-b grid grid-cols-7 text-xs font-semibold text-slate-500 uppercase gap-2">
              <span>{t('common.date','Date')}</span><span>Weeks</span><span>Weight</span><span>BP</span><span>FHR</span><span>Hb</span><span>Next Visit</span>
            </div>
            {ancVisits.length === 0 ? <div className="p-8 text-center text-slate-400">No ANC visits recorded</div> :
              <div className="divide-y max-h-[60vh] overflow-y-auto">
                {ancVisits.map((v: any) => (
                  <div key={v.id} className="grid grid-cols-7 gap-2 p-3 items-center hover:bg-slate-50 text-sm">
                    <span>{fmtDate(v.visit_date || v.visitDate)}</span>
                    <span>{v.gestational_weeks || v.gestationalWeeks || '—'}</span>
                    <span>{v.weight_kg || v.weightKg || '—'} kg</span>
                    <span>{v.blood_pressure || v.bloodPressure || '—'}</span>
                    <span>{v.fetal_heart_rate || v.fetalHeartRate || '—'}</span>
                    <span>{v.hemoglobin || '—'}</span>
                    <span className="text-xs text-slate-500">{v.next_visit_date || v.nextVisitDate ? fmtDate(v.next_visit_date || v.nextVisitDate) : '—'}</span>
                  </div>
                ))}
              </div>}
          </div>
        )
      )}

      {tab === 'deliveries' && (
        <div className="flex gap-4">
          <div className="w-80 flex-shrink-0">
            <div className="card overflow-hidden">
              <div className="p-3 bg-slate-50 border-b text-xs font-semibold text-slate-600 uppercase">Delivery Records</div>
              {deliveries.length === 0 ? <div className="p-4 text-center text-slate-400 text-sm">No deliveries</div> :
                <div className="divide-y max-h-[65vh] overflow-y-auto">
                  {deliveries.map((d: any) => (
                    <div key={d.id} onClick={() => openDeliveryDetail(d)} className={`p-3 cursor-pointer hover:bg-slate-50 transition-colors ${selDelivery?.id === d.id ? 'bg-blue-50 border-l-4 border-[#015CA6]' : ''}`}>
                      <p className="font-medium text-sm">{d.first_name} {d.last_name}</p>
                      <p className="text-xs text-slate-400">{fmtDateTime(d.delivered_at || d.deliveredAt)}</p>
                      <div className="flex gap-1 mt-1">
                        <Badge label={d.mode_of_delivery || d.modeOfDelivery} className="bg-blue-50 text-[#02357A] text-xs" />
                        <Badge label={d.outcome} className={d.outcome === 'LIVE_BIRTH' ? 'bg-green-100 text-green-700 text-xs' : 'bg-red-100 text-red-700 text-xs'} />
                      </div>
                    </div>
                  ))}
                </div>}
            </div>
          </div>
          {!selDelivery ? (
            <div className="flex-1 flex items-center justify-center text-slate-300">
              <div className="text-center"><p className="text-6xl mb-3">🏥</p><p>Select a delivery record to view partograph & newborn details</p></div>
            </div>
          ) : (
            <div className="flex-1 space-y-3 overflow-y-auto max-h-[70vh]">
              <div className="card p-3 bg-gradient-to-r from-[#02357A] to-indigo-800 text-white">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-bold text-lg">{selDelivery.first_name} {selDelivery.last_name}</p>
                    <p className="text-blue-200 text-sm">{fmtDateTime(selDelivery.delivered_at || selDelivery.deliveredAt)} · {selDelivery.mode_of_delivery || selDelivery.modeOfDelivery}</p>
                  </div>
                  <div className="flex gap-1 items-start">
                    <button onClick={() => setShowPartograph(true)} className="bg-white/20 hover:bg-white/30 text-xs px-2 py-1 rounded transition-colors">+ Partograph</button>
                    <button onClick={() => { setNewbornForm(defaultNewbornForm); setShowNewborn(true); }} className="bg-white/20 hover:bg-white/30 text-xs px-2 py-1 rounded transition-colors">+ Newborn</button>
                    <FileShaClaimButton
                      patientId={selDelivery.patient_id || selDelivery.patientId}
                      patientName={`${selDelivery.first_name || ''} ${selDelivery.last_name || ''}`.trim()}
                      encounterId={selDelivery.id}
                      serviceName={(selDelivery.mode_of_delivery || selDelivery.modeOfDelivery) === 'CAESAREAN_SECTION' ? 'Caesarean section' : 'Normal delivery'}
                      serviceType="INPATIENT"
                      from="maternity-delivery"
                      className="bg-white/20 hover:bg-white/30 text-xs px-2 py-1 rounded transition-colors"
                    />
                  </div>
                </div>
                {selDelivery.complications && <p className="text-blue-100 text-xs mt-2 italic">⚠️ {selDelivery.complications}</p>}
              </div>

              <div className="card p-3">
                <h3 className="font-semibold text-sm mb-2">📈 Partograph Log ({partographLog.length} entries)</h3>
                {partographLog.length === 0 ? <p className="text-sm text-slate-400">No entries yet</p> :
                  <table className="w-full text-xs text-center">
                    <thead className="bg-slate-50"><tr><th className="p-1.5">{t('common.time','Time')}</th><th>Cervical Dilation</th><th>Contractions/10min</th><th>FHR</th><th>{t('common.notes','Notes')}</th></tr></thead>
                    <tbody>
                      {partographLog.map((e: any, i: number) => (
                        <tr key={i} className="border-t">
                          <td className="p-1.5">{e.time}</td><td>{e.cervicalDilationCm} cm</td><td>{e.contractionsPer10Min}</td><td>{e.fetalHeartRate || '—'}</td><td>{e.notes || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>}
              </div>

              <div className="card p-3">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-semibold text-sm">👶 Newborns ({newborns.length})</h3>
                </div>
                {newborns.length === 0 ? <p className="text-sm text-slate-400">No newborn records yet</p> :
                  <div className="space-y-2">
                    {newborns.map((n: any) => (
                      <div key={n.id} className="bg-slate-50 rounded-lg p-2.5 text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium">{n.baby_name || n.babyName || 'Unnamed'} ({n.sex})</span>
                          <Badge label={n.status} className={n.status === 'ALIVE' ? 'bg-green-100 text-green-700 text-xs' : 'bg-red-100 text-red-700 text-xs'} />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Birth wt: {n.birth_weight_kg || n.birthWeightKg} kg · APGAR: {n.apgar1_min || n.apgar1Min}/{n.apgar5_min || n.apgar5Min}/{n.apgar10_min || n.apgar10Min || '—'}</p>
                        {(n.birth_notification_number || n.birthNotificationNumber) && <p className="text-xs text-teal-700 mt-1">🛡️ Birth Notification: {n.birth_notification_number || n.birthNotificationNumber}</p>}
                      </div>
                    ))}
                  </div>}
              </div>
            </div>
          )}
        </div>
      )}

      <SlidePanel isOpen={showAnc} onClose={() => setShowAnc(false)} title="New ANC Visit" icon="🤰" size="lg"
        actions={<button onClick={saveAnc} disabled={saving} className="btn-primary w-full">{saving ? 'Saving...' : 'Save ANC Visit'}</button>}>
        <div className="space-y-3">
          {patient && <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-sm text-[#02357A]">Patient: <strong>{patient.firstName || patient.first_name} {patient.lastName || patient.last_name}</strong></div>}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Visit Date *</label><Input type="date" value={ancForm.visitDate} onChange={e => setAncForm({ ...ancForm, visitDate: e.target.value })} /></div>
            <div><label className="label">Gestational Weeks</label><Input type="number" value={ancForm.gestationalWeeks} onChange={e => setAncForm({ ...ancForm, gestationalWeeks: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="label">Weight (kg)</label><Input type="number" step="0.1" value={ancForm.weightKg} onChange={e => setAncForm({ ...ancForm, weightKg: e.target.value })} /></div>
            <div><label className="label">Blood Pressure</label><Input value={ancForm.bloodPressure} onChange={e => setAncForm({ ...ancForm, bloodPressure: e.target.value })} placeholder="120/80" /></div>
            <div><label className="label">Fundal Height (cm)</label><Input type="number" value={ancForm.fundalHeightCm} onChange={e => setAncForm({ ...ancForm, fundalHeightCm: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="label">Fetal Heart Rate</label><Input type="number" value={ancForm.fetalHeartRate} onChange={e => setAncForm({ ...ancForm, fetalHeartRate: e.target.value })} /></div>
            <div><label className="label">Hemoglobin</label><Input type="number" step="0.1" value={ancForm.hemoglobin} onChange={e => setAncForm({ ...ancForm, hemoglobin: e.target.value })} /></div>
            <div><label className="label">Urinalysis</label><Input value={ancForm.urinalysis} onChange={e => setAncForm({ ...ancForm, urinalysis: e.target.value })} /></div>
          </div>
          <div><label className="label">Fetal Presentation</label><Input value={ancForm.fetalPresentation} onChange={e => setAncForm({ ...ancForm, fetalPresentation: e.target.value })} placeholder="Cephalic / Breech / Transverse" /></div>
          <div className="flex items-center gap-2"><input type="checkbox" checked={ancForm.tetanusToxoidGiven} onChange={e => setAncForm({ ...ancForm, tetanusToxoidGiven: e.target.checked })} /><label className="text-sm">Tetanus Toxoid Given</label></div>
          <div><label className="label">Complaints</label><Textarea rows={2} value={ancForm.complaints} onChange={e => setAncForm({ ...ancForm, complaints: e.target.value })} /></div>
          <div><label className="label">{t('common.notes','Notes')}</label><Textarea rows={2} value={ancForm.notes} onChange={e => setAncForm({ ...ancForm, notes: e.target.value })} /></div>
          <div><label className="label">Next Visit Date</label><Input type="date" value={ancForm.nextVisitDate} onChange={e => setAncForm({ ...ancForm, nextVisitDate: e.target.value })} /></div>
        </div>
      </SlidePanel>

      <SlidePanel isOpen={showDelivery} onClose={() => setShowDelivery(false)} title="New Delivery Record" icon="🏥" size="lg"
        actions={<button onClick={saveDelivery} disabled={saving} className="btn-primary w-full">{saving ? 'Saving...' : 'Create Delivery Record'}</button>}>
        <div className="space-y-3">
          {patient && <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-sm text-[#02357A]">Patient: <strong>{patient.firstName || patient.first_name} {patient.lastName || patient.last_name}</strong></div>}
          <div><label className="label">Delivered At *</label><Input type="datetime-local" value={deliveryForm.deliveredAt} onChange={e => setDeliveryForm({ ...deliveryForm, deliveredAt: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Onset Type</label>
              <Select value={deliveryForm.onsetType} onChange={e => setDeliveryForm({ ...deliveryForm, onsetType: e.target.value })}>
                {['SPONTANEOUS', 'INDUCED', 'ELECTIVE_CS'].map(o => <option key={o}>{o}</option>)}
              </Select>
            </div>
            <div><label className="label">Mode of Delivery</label>
              <Select value={deliveryForm.modeOfDelivery} onChange={e => setDeliveryForm({ ...deliveryForm, modeOfDelivery: e.target.value })}>
                {MODE_OF_DELIVERY.map(o => <option key={o}>{o}</option>)}
              </Select>
            </div>
          </div>
          {deliveryForm.modeOfDelivery === 'CAESAREAN_SECTION' && (
            <div><label className="label">Caesarean Indication</label><Input value={deliveryForm.caesareanIndication} onChange={e => setDeliveryForm({ ...deliveryForm, caesareanIndication: e.target.value })} /></div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Perineal Status</label><Input value={deliveryForm.perinealStatus} onChange={e => setDeliveryForm({ ...deliveryForm, perinealStatus: e.target.value })} placeholder="Intact / Episiotomy / Tear" /></div>
            <div><label className="label">Placenta Delivery</label><Input value={deliveryForm.placentaDelivery} onChange={e => setDeliveryForm({ ...deliveryForm, placentaDelivery: e.target.value })} placeholder="Spontaneous / Manual" /></div>
          </div>
          <div><label className="label">Estimated Blood Loss (ml)</label><Input type="number" value={deliveryForm.estimatedBloodLossMl} onChange={e => setDeliveryForm({ ...deliveryForm, estimatedBloodLossMl: e.target.value })} /></div>
          <div><label className="label">Outcome</label>
            <Select value={deliveryForm.outcome} onChange={e => setDeliveryForm({ ...deliveryForm, outcome: e.target.value })}>
              {OUTCOMES.map(o => <option key={o}>{o}</option>)}
            </Select>
          </div>
          <div><label className="label">Complications</label><Textarea rows={2} value={deliveryForm.complications} onChange={e => setDeliveryForm({ ...deliveryForm, complications: e.target.value })} /></div>
          <div><label className="label">{t('common.notes','Notes')}</label><Textarea rows={2} value={deliveryForm.notes} onChange={e => setDeliveryForm({ ...deliveryForm, notes: e.target.value })} /></div>
        </div>
      </SlidePanel>

      <SlidePanel isOpen={showPartograph} onClose={() => setShowPartograph(false)} title="Add Partograph Entry" icon="📈"
        actions={<button onClick={savePartograph} disabled={saving} className="btn-primary w-full">{saving ? 'Saving...' : 'Add Entry'}</button>}>
        <div className="space-y-3">
          <div><label className="label">{t('common.time','Time')}</label><Input type="time" value={partographForm.time} onChange={e => setPartographForm({ ...partographForm, time: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Cervical Dilation (cm)</label><Input type="number" value={partographForm.cervicalDilationCm} onChange={e => setPartographForm({ ...partographForm, cervicalDilationCm: e.target.value })} /></div>
            <div><label className="label">Contractions / 10min</label><Input type="number" value={partographForm.contractionsPer10Min} onChange={e => setPartographForm({ ...partographForm, contractionsPer10Min: e.target.value })} /></div>
          </div>
          <div><label className="label">Fetal Heart Rate</label><Input type="number" value={partographForm.fetalHeartRate} onChange={e => setPartographForm({ ...partographForm, fetalHeartRate: e.target.value })} /></div>
          <div><label className="label">{t('common.notes','Notes')}</label><Input value={partographForm.notes} onChange={e => setPartographForm({ ...partographForm, notes: e.target.value })} /></div>
        </div>
      </SlidePanel>

      <SlidePanel isOpen={showNewborn} onClose={() => setShowNewborn(false)} title="New Newborn Record" icon="👶" size="lg"
        actions={<button onClick={saveNewborn} disabled={saving} className="btn-primary w-full">{saving ? 'Saving...' : 'Create Newborn Record'}</button>}>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Baby Name</label><Input value={newbornForm.babyName} onChange={e => setNewbornForm({ ...newbornForm, babyName: e.target.value })} /></div>
            <div><label className="label">Sex</label>
              <Select value={newbornForm.sex} onChange={e => setNewbornForm({ ...newbornForm, sex: e.target.value })}>
                <option>FEMALE</option><option>MALE</option><option>AMBIGUOUS</option>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Birth Weight (kg)</label><Input type="number" step="0.001" value={newbornForm.birthWeightKg} onChange={e => setNewbornForm({ ...newbornForm, birthWeightKg: e.target.value })} /></div>
            <div><label className="label">Birth Time</label><Input type="datetime-local" value={newbornForm.birthTime} onChange={e => setNewbornForm({ ...newbornForm, birthTime: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="label">APGAR 1min</label><Input type="number" min="0" max="10" value={newbornForm.apgar1Min} onChange={e => setNewbornForm({ ...newbornForm, apgar1Min: e.target.value })} /></div>
            <div><label className="label">APGAR 5min</label><Input type="number" min="0" max="10" value={newbornForm.apgar5Min} onChange={e => setNewbornForm({ ...newbornForm, apgar5Min: e.target.value })} /></div>
            <div><label className="label">APGAR 10min</label><Input type="number" min="0" max="10" value={newbornForm.apgar10Min} onChange={e => setNewbornForm({ ...newbornForm, apgar10Min: e.target.value })} /></div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={newbornForm.resuscitationRequired} onChange={e => setNewbornForm({ ...newbornForm, resuscitationRequired: e.target.checked })} />Resuscitation Required</label>
          </div>
          {newbornForm.resuscitationRequired && <div><label className="label">Resuscitation Details</label><Input value={newbornForm.resuscitationDetails} onChange={e => setNewbornForm({ ...newbornForm, resuscitationDetails: e.target.value })} /></div>}
          <div><label className="label">Congenital Anomalies</label><Input value={newbornForm.congenitalAnomalies} onChange={e => setNewbornForm({ ...newbornForm, congenitalAnomalies: e.target.value })} /></div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={newbornForm.vitaminKGiven} onChange={e => setNewbornForm({ ...newbornForm, vitaminKGiven: e.target.checked })} />Vitamin K Given</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={newbornForm.bcgGiven} onChange={e => setNewbornForm({ ...newbornForm, bcgGiven: e.target.checked })} />BCG Given</label>
          </div>
          <div><label className="label">Feeding Type</label>
            <Select value={newbornForm.feedingType} onChange={e => setNewbornForm({ ...newbornForm, feedingType: e.target.value })}>
              {FEEDING_TYPES.map(f => <option key={f}>{f}</option>)}
            </Select>
          </div>
          <div><label className="label">Birth Notification Number</label><Input value={newbornForm.birthNotificationNumber} onChange={e => setNewbornForm({ ...newbornForm, birthNotificationNumber: e.target.value })} placeholder="For DHA manual claim attachment" /></div>
          <div><label className="label">{t('common.notes','Notes')}</label><Textarea rows={2} value={newbornForm.notes} onChange={e => setNewbornForm({ ...newbornForm, notes: e.target.value })} /></div>
        </div>
      </SlidePanel>
    </Layout>
  );
}
